import { ProjectStatus, SupplierApprovalStatus, isPendingProjectStatus } from '@/models/common/ProjectStatus';

/**
 * Formats a project amount as ILS currency.
 *
 * Project amounts are plain numbers, so they cannot go through Cell(cellType.Amount) - that
 * helper destructures the cell value as an object and throws on a bare number. Keeping the
 * accessor a number also means the column still sorts numerically.
 */
export const formatProjectAmount = (amount: number, language: string): string =>
  (amount ?? 0).toLocaleString(language, { style: 'currency', currency: 'ILS' });

/**
 * Colour of the status dot next to a project/supplier status, matching the palette the
 * support-request statuses use (see helpers/requestStatus.ts).
 */
export const getProjectStatusColor = (status: ProjectStatus): string => {
  if (status === ProjectStatus.Approved) return 'var(--icon-status-6)';
  if (status === ProjectStatus.Rejected) return 'var(--icon-status-4)';
  // The colour support requests use for "returned for completion" - also waiting on the coordinator.
  if (status === ProjectStatus.PendingCoordinatorResponse) return 'var(--icon-status-2)';
  return 'var(--icon-status-1)';
};

export const getSupplierApprovalStatusColor = (status: SupplierApprovalStatus): string => {
  if (status === SupplierApprovalStatus.Approved) return 'var(--icon-status-6)';
  if (status === SupplierApprovalStatus.Rejected) return 'var(--icon-status-4)';
  return 'var(--icon-status-1)';
};

/**
 * i18n key of a project status label. The three Pending* sub-stages keep their own names in the
 * blade timeline, but a list cell shows the aggregate "ממתין לאישור" — a coordinator does not
 * need to know which committee currently holds the request.
 */
export const getProjectStatusLabelKey = (status: ProjectStatus, aggregatePending = true): string => {
  if (!aggregatePending) return `projects.status.${status}`;
  if (isPendingProjectStatus(status)) return 'projects.status.pending';
  // The coordinator's view of the office's question: it waits for them.
  if (status === ProjectStatus.PendingCoordinatorResponse) return 'projects.status.waitingForYou';
  return `projects.status.${status}`;
};
